import { PlanMealItemCommonType } from "../model/nutritionPlanTypes";
import {
  calculateRecipeMacros,
  formatDifficulty,
  formatPrepTime,
  getPortionInfo,
} from "./mealItemHelpers";
import {
  getCalories,
  getPhotoUrl,
  getTitle,
  isFoodItem,
  isLiked,
} from "./planSelectors";

/**
 * Card-level state derived from a meal item, shared by the daily and weekly
 * cards. Ported from the mobile `components/shared/mealItemStateHelpers.ts`;
 * the store-function injection is gone because `planSelectors` are plain
 * functions here.
 */

export interface MealItemState {
  isFoodItem: boolean;
  isLogged: boolean;
  isLiked: boolean;
  isSkipped: boolean;
  isPersonalized: boolean;
  title: string;
  calories: number;
  photoUrl: string;
}

export interface MacroValues {
  protein: number;
  fat: number;
  carbs: number;
}

/** Status pill, in priority order: skipped > swapped > extras > personalized. */
export type StatusTagType =
  | "skipped"
  | "swapped"
  | "extras"
  | "personalized"
  | null;

/** A food is personalized by flag; a recipe when any ingredient is. */
export const getIsPersonalized = (
  mealItem: PlanMealItemCommonType
): boolean => {
  if (isFoodItem(mealItem)) return mealItem.food!.is_personalized === true;
  return (
    mealItem.recipe?.items?.some(
      (item) => item.food?.is_personalized === true
    ) ?? false
  );
};

export const getMealItemState = (
  mealItem: PlanMealItemCommonType
): MealItemState => ({
  isFoodItem: isFoodItem(mealItem),
  isLogged: mealItem.is_logged === true,
  isLiked: isLiked(mealItem),
  isSkipped: mealItem.is_skipped === true,
  isPersonalized: getIsPersonalized(mealItem),
  title: getTitle(mealItem),
  calories: getCalories(mealItem),
  photoUrl: getPhotoUrl(mealItem),
});

/** Whole-number macros: a food's own values, or a recipe summed over ingredients. */
export const getMacros = (mealItem: PlanMealItemCommonType): MacroValues => {
  if (isFoodItem(mealItem)) {
    const food = mealItem.food!;
    return {
      protein: Math.round(food.protein || 0),
      fat: Math.round(food.fat || 0),
      carbs: Math.round(food.carbs || 0),
    };
  }
  if (mealItem.recipe) {
    const macros = calculateRecipeMacros(mealItem.recipe);
    return {
      protein: Math.round(macros.protein),
      fat: Math.round(macros.fat),
      carbs: Math.round(macros.carbs),
    };
  }
  return { protein: 0, fat: 0, carbs: 0 };
};

export const getStatusTagType = (
  mealItem: PlanMealItemCommonType,
  isExtra = false,
  isAlternative = false
): StatusTagType => {
  if (mealItem.is_skipped) return "skipped";
  if (isAlternative) return "swapped";
  if (isExtra) return "extras";
  if (getIsPersonalized(mealItem)) return "personalized";
  return null;
};

export const getRecipeInfo = (
  mealItem: PlanMealItemCommonType
): { prepTime: string | null; difficultyLevel: number } => {
  if (!isFoodItem(mealItem) && mealItem.recipe) {
    return {
      prepTime: mealItem.recipe.prepare_duration || null,
      difficultyLevel: mealItem.recipe.difficulty_level || 2,
    };
  }
  return { prepTime: null, difficultyLevel: 2 };
};

/** "30 min • Easy" for recipes; "" for foods. */
export const getRecipeDescriptionText = (
  mealItem: PlanMealItemCommonType
): string => {
  if (isFoodItem(mealItem) || !mealItem.recipe) return "";
  const { prepTime, difficultyLevel } = getRecipeInfo(mealItem);
  const time = prepTime ? formatPrepTime(prepTime) : "-- min";
  return `${time} • ${formatDifficulty(difficultyLevel)}`;
};

/**
 * The line under the card title.
 * Food: "1 Cup • 240 kcal". Recipe: "30 min • Easy • 350 kcal".
 */
export const getInfoLine = (
  mealItem: PlanMealItemCommonType,
  calories: number
): string => {
  if (isFoodItem(mealItem)) {
    return `${getPortionInfo(mealItem.food)} • ${calories} kcal`;
  }
  return `${getRecipeDescriptionText(mealItem)} • ${calories} kcal`;
};
